// Counsel-review verdict detail (DESIGN-VS-BUILD-GAPS-R2 §B) — the drill-in the queue lacked. The list could
// only quick-triage (no notes, no sight of the draft); here the reviewer reads the actual section draft + the
// AI prefill-run context, sees the prior verdict (notes/decidedAt), and records a verdict WITH notes.
import { useState } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText } from "lucide-react";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface CounselDetail {
  id: string; status: string; contentType: string | null; aiSelfCheckScore: string | null;
  createdAt: string; decidedAt: string | null; reviewerUserId: string | null; verdictNotes: string | null;
  prefillRunId: string; sectionDraftId: string | null;
  run: { scopeKind: string; status: string; engineVersion: string; templateId: string; triggeredKind: string | null; overallSelfCheckScore: string | null; completedAt: string | null } | null;
  draft: { sectionCode: string; selfCheckScore: string | null; draftContentMarkdown: string | null } | null;
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  pending: "secondary", approved: "default", changes_requested: "outline", rejected: "destructive",
};
function fmt(d: string | null): string { return d ? d.slice(0, 16).replace("T", " ") : "—"; }
function score(s: string | null): string { return s != null ? Number(s).toFixed(3) : "—"; }

export function CounselReviewDetailRoute() {
  const { reviewId } = useParams({ from: "/counsel/$reviewId" });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [notes, setNotes] = useState("");

  const { data, isLoading, error } = useQuery({ queryKey: ["counsel-detail", reviewId], queryFn: () => api.getCounselReviewDetail(reviewId) as unknown as Promise<CounselDetail> });
  const d = data;

  const verdict = useMutation({
    mutationFn: (v: "approved" | "changes_requested" | "rejected") => api.submitCounselVerdict(reviewId, v, notes.trim() || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["counsel-reviews"] });
      qc.invalidateQueries({ queryKey: ["counsel-detail", reviewId] });
      navigate({ to: "/counsel" });
    },
  });
  const pending = d?.status === "pending";

  return (
    <section>
      <Link to="/counsel" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Back to counsel queue
      </Link>

      {isLoading ? (
        <div className="space-y-3"><Skeleton className="h-9 w-2/3" /><Skeleton className="h-40 w-full" /></div>
      ) : error ? (
        <p className="text-sm text-destructive">Failed: {error instanceof ApiError ? `${error.status}` : (error as Error).message}</p>
      ) : !d ? (
        <p className="text-sm text-muted-foreground">Review not found.</p>
      ) : (
        <>
          <PageHeader title={d.contentType ? humanize(d.contentType) : "Counsel review"} description={`Prefill run ${d.prefillRunId.slice(0, 8)}…`} />
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Badge variant={STATUS_VARIANT[d.status] ?? "secondary"}>{humanize(d.status)}</Badge>
            <Badge variant="outline" className={Number(d.aiSelfCheckScore) < 0.7 ? "text-destructive" : undefined}>self-check {score(d.aiSelfCheckScore)}</Badge>
            {d.decidedAt && <Badge variant="outline">decided {fmt(d.decidedAt)}</Badge>}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* The draft under review — the content the reviewer must read */}
            <Card className="lg:row-span-2">
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><FileText className="size-4" />Draft under review {d.draft?.sectionCode ? <Badge variant="outline">{d.draft.sectionCode}</Badge> : null}</CardTitle></CardHeader>
              <CardContent>
                {d.draft?.draftContentMarkdown
                  ? <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-3 text-sm font-sans">{d.draft.draftContentMarkdown}</pre>
                  : <p className="text-sm text-muted-foreground">No section draft linked to this review.</p>}
                {d.draft?.selfCheckScore != null && <p className="mt-2 text-xs text-muted-foreground">Draft self-check: {score(d.draft.selfCheckScore)}</p>}
              </CardContent>
            </Card>

            {/* Prefill-run context */}
            <Card>
              <CardHeader><CardTitle className="text-base">Prefill run</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {d.run ? (
                  <>
                    <div className="flex justify-between gap-3"><span className="text-muted-foreground">Scope</span><span>{humanize(d.run.scopeKind)}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-muted-foreground">Status</span><span>{humanize(d.run.status)}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-muted-foreground">Triggered by</span><span>{d.run.triggeredKind ? humanize(d.run.triggeredKind) : "—"}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-muted-foreground">Engine</span><span>{d.run.engineVersion}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-muted-foreground">Overall self-check</span><span>{score(d.run.overallSelfCheckScore)}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-muted-foreground">Completed</span><span>{fmt(d.run.completedAt)}</span></div>
                  </>
                ) : <p className="text-muted-foreground">Prefill run not found.</p>}
              </CardContent>
            </Card>

            {/* Verdict — record with notes (the list could only quick-triage without notes) */}
            <Card>
              <CardHeader><CardTitle className="text-base">{pending ? "Record verdict" : "Verdict"}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {d.verdictNotes && <p className="rounded bg-muted/40 p-2 text-sm">{d.verdictNotes}</p>}
                {pending ? (
                  <>
                    <textarea
                      value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Verdict notes (optional)…"
                      className="min-h-[5rem] w-full rounded-md border bg-background p-2 text-sm"
                      aria-label="Verdict notes"
                    />
                    {verdict.isError && <p className="text-sm text-destructive">{verdict.error instanceof ApiError ? `Server ${verdict.error.status}` : (verdict.error as Error).message}</p>}
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" disabled={verdict.isPending} onClick={() => verdict.mutate("approved")}>Approve</Button>
                      <Button size="sm" variant="outline" disabled={verdict.isPending} onClick={() => verdict.mutate("changes_requested")}>Request changes</Button>
                      <Button size="sm" variant="destructive" disabled={verdict.isPending} onClick={() => verdict.mutate("rejected")}>Reject</Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">This review was decided ({d.status}){d.reviewerUserId ? ` by ${d.reviewerUserId.slice(0, 8)}…` : ""}.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </section>
  );
}
